import type { components } from '@multaqa/api-client'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * The typed boundary to `GET /purchasing/reports/three-way-match` (trade
 * plan 8, screen 4).
 *
 * `not_matched` never appears in this report — the backend query INNER JOINs
 * the match figures, so a bill line with no allocation has no row to join to
 * (`ThreeWayMatchVarianceReportQuery`). `VARIANCE_KINDS` below is the four
 * statuses that can actually show up, and it is what the filter offers.
 *
 * Every quantity and money figure is a decimal string, handed straight to
 * `formatQuantity` / `formatMoney`; nothing in this file parses one. `totals`
 * and `company_total` are the backend's own sums (D5, D6, R1) — never
 * recomputed here.
 */
export type ThreeWayMatchReport = components['schemas']['ThreeWayMatchVarianceReportResource']
export type ThreeWayMatchRow = components['schemas']['ThreeWayMatchVarianceRowResource']
export type MatchStatus = components['schemas']['MatchStatus']

/** The only match statuses a bill line allocated to a receipt can end up with. */
export const VARIANCE_KINDS: MatchStatus[] = [
  'matched',
  'quantity_variance',
  'price_variance',
  'quantity_and_price_variance',
]

/** The filter surface this report accepts, as URL search params (R6). */
export interface ThreeWayMatchSearch {
  supplier_id?: string
  from?: string
  to?: string
  variance_kind?: MatchStatus
  min_absolute_value_variance?: number
}

/**
 * Validate raw URL search params into a `ThreeWayMatchSearch`, dropping
 * anything that is not a well-formed filter. Mirrors `reportSearchFrom` in
 * `accounting-reports/api.ts`.
 */
export function threeWayMatchSearchFrom(search: Record<string, unknown>): ThreeWayMatchSearch {
  const rawMin = search.min_absolute_value_variance
  const parsedMin =
    typeof rawMin === 'number'
      ? rawMin
      : typeof rawMin === 'string' && rawMin !== ''
        ? Number(rawMin)
        : undefined

  return {
    supplier_id:
      typeof search.supplier_id === 'string' && search.supplier_id !== ''
        ? search.supplier_id
        : undefined,
    from: typeof search.from === 'string' && search.from !== '' ? search.from : undefined,
    to: typeof search.to === 'string' && search.to !== '' ? search.to : undefined,
    variance_kind:
      typeof search.variance_kind === 'string' &&
      (VARIANCE_KINDS as string[]).includes(search.variance_kind)
        ? (search.variance_kind as MatchStatus)
        : undefined,
    min_absolute_value_variance:
      parsedMin !== undefined && Number.isFinite(parsedMin) && parsedMin >= 0
        ? parsedMin
        : undefined,
  }
}

export function useThreeWayMatchReport(
  companyId: string | null,
  search: ThreeWayMatchSearch,
  options?: Omit<UseQueryOptions<ThreeWayMatchReport, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ThreeWayMatchReport, Error>({
    queryKey: ['companies', companyId, 'purchasing', 'reports', 'three-way-match', search] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<ThreeWayMatchReport> => {
      if (!companyId) {
        throw new Error('Cannot load the three-way match report without an active company.')
      }
      const response = await api.GET('/purchasing/reports/three-way-match', {
        headers: { 'X-Company-Id': companyId },
        params: { query: search },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to load the three-way match report')
      }
      if (!response.data?.data) {
        throw new Error('Malformed three-way match report response.')
      }
      // `columns` is a fixed 18-element tuple in the schema; openapi-fetch's
      // own response-typing widens it to a plain array on the way through,
      // so the cast below is a width fix, not a type escape — the same one
      // `accounting-reports/api.ts`'s `unwrap()` applies for the same reason.
      return response.data.data as ThreeWayMatchReport
    },
  })
}
